import { DurationString } from '@chronosphereio/core';
import { ProjectMetadata } from './resource';
import { ListVariableDefinition, QueryResolutionLevel, VariableDefinition } from './schema';

export type VariableName = string;

export type VariableValue = string | string[] | null;

export type ListVariableSpec = ListVariableDefinition['spec'];

/**
 * A variable that belongs to a project.
 */
export interface VariableResource {
  kind: 'Variable';
  metadata: ProjectMetadata;
  spec: VariableDefinition;
}

export const VAR_QUERY_PARAM_PREFIX = 'var-';

// Escape commas in values to support multi-value variables
function escapeCommas(value: string) {
  return value.replace(/,/g, '\\,');
}

export function encodeVariableValue(value: VariableValue) {
  if (Array.isArray(value)) {
    return value.join(',');
  }
  return escapeCommas(value ?? '');
}

export type VariableOption = { label: string; value: string };

export type VariableState = {
  value: VariableValue;
  options?: VariableOption[];
  loading: boolean;
  error?: Error;
  // HasLoaded is used to keep track of whether the variable has finished it's initial loading of options
  // This is used to ensure custom variables (ie variables not in the initial list of options) are not
  // reset upon the initial load
  hasLoaded?: boolean;
  /**
   * This key is used to track the last set of variable values that this variable depended on. If this value changes, it means
   * that the variable should be reset as the values it depends have changed.
   */
  lastDependsOnVariableValuesKey?: string;
};

export type VariableStateMap = Record<VariableName, VariableState>;

export const INTERVAL_VARIABLE_AUTO_VALUE = '__auto';
export const DEFAULT_INTERVAL_VARIABLE_OPTIONS: DurationString[] = [
  '1m',
  '5m',
  '10m',
  '30m',
  '1h',
  '6h',
  '12h',
  '1d',
  '7d',
  '14d',
  '30d',
];

/**
 * Default resolution for internal calculations when resolution is not specified.
 * This is used by calculateAutoIntervalSeconds as a fallback.
 */
export const DEFAULT_INTERVAL_VARIABLE_RESOLUTION: QueryResolutionLevel = 'Medium';

export const DEFAULT_MIN_INTERVAL_SIZE: DurationString = '1m';

export function isListVariableDefinition(variable: VariableDefinition | undefined): variable is ListVariableDefinition {
  return variable?.kind === 'ListVariable';
}
